import React, { useState } from "react";

import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Typography } from "@mui/material";

import Anchor from "../../models/anchors/Anchor";
import AnchorPropertyForm from "./AnchorPropertyEditor";
import EditorWrapper from "../ui/EditorWrapper";
import { useRowSelector } from "../../store/Hooks";
import AnchorProperties from "../../models/anchors/AnchorProperties";

interface AnchorEditorProps {
  anchor_id: number;
}

const AnchorEditor = (props: AnchorEditorProps) => {
  const anchor = useRowSelector(Anchor, props.anchor_id); // can replace this with a working id to check

  // this causes hook rendering error when clicking Save in editor
  //   if (typeof anchor === "undefined") {
  //   return <></>;
  // }

  const newPropsMap : Map<string, any> = anchor ? anchor.properties.properties : new Map();

  const addProperty = () => {
    newPropsMap.set("NewItem","any")
    const insertObj = {properties:newPropsMap} as AnchorProperties
    anchor?.update("properties", insertObj);
  };

  const deleteProperty = (index: number, arrayIndex: number | undefined) => {
    const anchorPropsCopy = anchor?.properties
    //const anchorPropsCopy = stateDeepCopy(anchor?.properties)
    const elements : any = Array.from(newPropsMap); // Convert to an array and pick the index
    const insertObj = anchorPropsCopy;

    if(typeof arrayIndex !== 'undefined'){
      elements[index][1].splice(arrayIndex, 1)
      const thisPropertyKey = elements[index][0]
      const updatedMap : Map<string, any> = new Map(elements);
      const currentValuesArray : Array<any>= updatedMap.get(thisPropertyKey) as unknown as any
      if(currentValuesArray.length < 1){
        updatedMap.delete(thisPropertyKey)
      }
      insertObj.properties = updatedMap

    } else {
      AnchorProperties.deleteEntryAtPosition(insertObj.properties,index)
    }

    anchor?.update("properties", insertObj);
  };

  const updateProperty = (index: number, objkey: any, value: any, arrayIndex:any, changeKey:boolean) => {
    // this is array of arrays, so its the array at index X, and then
    // the first val in that array is the key, second is the value
    const elements : any = Array.from(newPropsMap); // Convert to an array and pick the index
    const thisCurrentValue = newPropsMap.get(objkey)
    
    // check if value is currently an array:
    const isArray = typeof thisCurrentValue === 'object'
    const insertObj = new AnchorProperties();
    elements[index][0] = objkey

    if(isArray){
      // see if there are other values in array, and update the one that needs updating
      if(thisCurrentValue.length > 0){
        if(typeof arrayIndex !== undefined && arrayIndex > -1){
          thisCurrentValue[arrayIndex] = value
        } else {
          thisCurrentValue.push(value)
        }
      }

      elements[index][1] = thisCurrentValue
    } else { // handle if value is string
      // if this property is already defined and is not already an array,
      // and the update type is that the user is updating the key on pair,
      // make it an array and save it

      if(thisCurrentValue && changeKey){
        elements[index][1] = [thisCurrentValue,value]
      } else {
        elements[index][1] = value
      }
    }
    const updatedMap : Map<string, any> = new Map(elements);
    insertObj.properties = updatedMap
    anchor?.update("properties", insertObj);
  };

  const saveAnchor = async () => {
    if (anchor?.subject_id === -1) {
      throw new Error("Subject ID must be set");
    }
    if (anchor && isNaN(new Date(anchor.date).getTime())) {
      throw new Error("The entered date is not valid");
    }
    await anchor?.save();
  };

  const propertyComponents = Array.from(newPropsMap).map(
    ([key, value], index) => {
      let tsx : any = <></>;
      if(Array.isArray(value)) {
        let iterator = -1;
        tsx = value.map((arrayValue, index2)=>{
          iterator++;
          return (
            <Grid item xs={12} key={index2}>
              <AnchorPropertyForm
                updateProperty={updateProperty}
                deleteProperty={deleteProperty}
                property={{ [key]: arrayValue }}
                index={index}
                arrayIndex={iterator}
              ></AnchorPropertyForm>
            </Grid>
            )
        })
        return tsx;
      } else {
        return (
          <Grid item xs={12} key={index}>
            <AnchorPropertyForm
              updateProperty={updateProperty}
              deleteProperty={deleteProperty}
              property={{ [key]: value }}
              index={index}
            ></AnchorPropertyForm>
          </Grid>
          )
      }

    }
  )
  const anchorDate = anchor ? new Date(anchor.date) : new Date();
  const yearString: string = anchorDate.getUTCFullYear().toString();
  const month: number = anchorDate.getUTCMonth() + 1;
  const monthString: string = month < 10 ? "0" + month : month.toString();
  const day: number = anchorDate.getUTCDate();
  const dayString: string = day < 10 ? "0" + day : day.toString();

  // const anchorDateString = yearString + "-" + monthString + "-" + dayString;
  const [anchorDateString, setAnchorDateString] = useState<string>(
    `${yearString}-${monthString}-${dayString}`
  );
      
  return (
    <EditorWrapper
      title="Edit Anchor"
      inDrawer={true}
      save={saveAnchor}
      getSaveMessage={() =>
        "Successfully saved anchor for patient " + anchor?.subject_id
      }
    >
      <Box sx={{ padding: "10px" }}>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={1}
          justifyContent="flex-start"
        >
          <Grid item xs={5}>
            <FormControl required sx={{ width: "100%" }}>
              <TextField
                name="subject_id"
                label="CareSense PatientID"
                type="number"
                required
                value={anchor && (anchor.subject_id > 0) ? anchor?.subject_id : ""}
                InputProps={{
                  inputProps: { min: 0 },
                }}
                sx={{ width: "100%" }}
                onChange={(event: React.ChangeEvent<any>) => {
                  if(anchor){
                    const id = parseInt(event.target.value);
                    if (!isNaN(id)) {
                      anchor.update("subject_id", id);
                    } else {
                      anchor.update("subject_id", -1);
                    }
                  }
   
                }}
              ></TextField>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl required sx={{ width: "100%" }}>
              <TextField
                name="date"
                label="Anchor Date"
                type="date"
                value={anchorDateString}
                onChange={(event: React.ChangeEvent<any>) => {
                  const inputDate = event.target.value;
                  setAnchorDateString(inputDate);
                  const newDate = new Date(event.target.value);
                  if (!isNaN(newDate.getTime())) {
                    newDate.setDate(newDate.getDate());
                    anchor?.update("date", newDate.toISOString());
                  }
                }}
              ></TextField>
            </FormControl>
          </Grid>
          <Grid item xs={10}>
            <Typography textAlign="center" color="gray">
              Anchor Properties
            </Typography>
          </Grid>
          {propertyComponents}
          <Grid item xs={10} textAlign="center">
            <IconButton color="primary" onClick={addProperty}>
              <AddIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
    </EditorWrapper>
  );
};

export default AnchorEditor;
